import React, { useState } from 'react';
import { Link } from 'gatsby';
import { motion } from 'motion/react';
import { convertUrltoCdn, getUtmSource } from '../utils/helpers';
import { kebabCase } from 'lodash';
import { CaretRight } from '@phosphor-icons/react';
import classNames from 'classnames';
import PlatformImg from '../../static/images/nav/platform.webp';
import SolutionsImg from '../../static/images/nav/solutions.webp';

const CARET_DOWN = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="9"
    height="6"
    viewBox="0 0 9 6"
    fill="none"
  >
    <path
      d="M1.5332 1.5L4.5332 4.5L7.5332 1.5"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PlatformContent = () => {
  return (
    <div className="mx-auto flex max-w-7xl gap-[72px]">
      {/* Image column */}
      <div className="w-1/4">
        <div className="aspect-square shrink-0 overflow-hidden rounded-2xl border border-black/10">
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            src={PlatformImg}
            alt="platform-illustration"
            className="h-full w-full bg-indigo-100 object-cover"
          />
        </div>
      </div>

      {/* Menu columns */}
      <div className="grid w-3/4 grid-cols-4 gap-8 text-sm">
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">Overview</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/platform/overview/"
              className="text-sm text-black hover:text-azure-800"
            >
              The Alloy Platform
            </Link>
            <Link
              to="/platform/why-alloy/"
              className="text-sm text-black hover:text-azure-800"
            >
              Why Alloy?
            </Link>
            <Link
              to="/platform/pricing/"
              className="text-sm text-black hover:text-azure-800"
            >
              Pricing
            </Link>
          </div>
        </div>
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">Capabilities</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/integrations/"
              className="text-sm text-black hover:text-azure-800"
            >
              Connector Library
            </Link>
            <Link
              to="/platform/ai-agents/"
              className="text-sm text-black hover:text-azure-800"
            >
              AI Agents
            </Link>
            <Link
              to="/platform/on-premise/"
              className="text-sm text-black hover:text-azure-800"
            >
              On Premise
            </Link>
            <Link
              to="/platform/error-handling/"
              className="text-sm text-black hover:text-azure-800"
            >
              Error Handling & Logging
            </Link>
            <Link
              to="/platform/data-transformations/"
              className="text-sm text-black hover:text-azure-800"
            >
              ETL/ELT
            </Link>
            <Link
              to="/platform/bespoke-customizations/"
              className="text-sm text-black hover:text-azure-800"
            >
              Bespoke Customizations
            </Link>
            <Link
              to="/platform/mcp/"
              className="text-sm text-black hover:text-azure-800"
            >
              MCP
            </Link>
          </div>
        </div>
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">Governance</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/platform/security/"
              className="text-sm text-black hover:text-azure-800"
            >
              Security and Compliance
            </Link>
            <Link
              to="/platform/data-governance/"
              className="text-sm text-black hover:text-azure-800"
            >
              Data Governance
            </Link>
            <Link
              to="/platform/scalability/"
              className="text-sm text-black hover:text-azure-800"
            >
              Enterprise Scalability
            </Link>
          </div>
        </div>
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">Products</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/platform/embedded/"
              className="text-sm text-black hover:text-azure-800"
            >
              Embedded Integrations
            </Link>
            <Link
              to="/platform/enterprise-ipaas/"
              className="text-sm text-black hover:text-azure-800"
            >
              Enterprise iPaaS
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SolutionsContent = () => {
  const [hoveredLink, setHoveredLink] = useState('');
  const [icon, setIcon] = useState('');

  return (
    <div className="mx-auto flex max-w-7xl gap-[72px]">
      {/* Image column */}
      <div className="w-1/4">
        <div className="aspect-square shrink-0 overflow-hidden rounded-2xl border border-black/10">
          {hoveredLink === 'integrations' ? (
            <motion.div
              key={hoveredLink}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className={classNames(
                'flex h-full w-full items-center justify-center bg-indigo-100',
                `bg-[url('../../static/images/nav/integrations.webp')] bg-cover bg-no-repeat`
              )}
            >
              {icon && (
                <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-black/10 bg-white">
                  <motion.img
                    key={icon}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    src={icon}
                    alt="icon"
                    className="h-11 w-11 object-contain mix-blend-multiply"
                  />
                </div>
              )}
            </motion.div>
          ) : (
            <motion.img
              key={hoveredLink}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              src={SolutionsImg}
              alt="integrations-illustration"
              className="flex h-full w-full items-center justify-center bg-indigo-100 object-cover"
            />
          )}
        </div>
      </div>

      {/* Menu columns */}
      <div className="grid w-3/4 grid-cols-4 gap-8 text-sm">
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">
            Popular Integrations
          </h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/solutions/popular-integrations/netsuite/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/netsuite.png');
              }}
            >
              NetSuite
            </Link>
            <Link
              to="/solutions/popular-integrations/sap-s4hana/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/sapS4Hana.png');
              }}
            >
              SAP (S/4HANA)
            </Link>
            <Link
              to="/solutions/popular-integrations/microsoft-dynamics-365-business-central/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/microsoft365.png');
              }}
            >
              Microsoft Dynamics 365
            </Link>
            <Link
              to="/solutions/popular-integrations/shopify/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/shopify.png');
              }}
            >
              Shopify
            </Link>
            <Link
              to="/solutions/popular-integrations/amazon/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/amazon.png');
              }}
            >
              Amazon
            </Link>
            <Link
              to="/solutions/popular-integrations/adp-workforce-now/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/ADPWorkforceNow.png');
              }}
            >
              ADP
            </Link>
            <Link
              to="/solutions/popular-integrations/service-now/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/serviceNow.png');
              }}
            >
              ServiceNow
            </Link>
            <Link
              to="/solutions/popular-integrations/salesforce/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/salesforce.png');
              }}
            >
              Salesforce
            </Link>
            <Link
              to="/solutions/popular-integrations/workday/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => {
                setHoveredLink('integrations');
                setIcon('https://cdn.runalloy.com/icons/workday.png');
              }}
            >
              Workday
            </Link>
            <Link
              to="/integrations/"
              className="text-sm text-azure-800"
              onMouseEnter={() => setHoveredLink('integrations')}
            >
              View All
            </Link>
          </div>
        </div>
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">By Function</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/solutions/by-function/for-it/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              IT
            </Link>
            <Link
              to="/solutions/by-function/for-product/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Product (Embedded)
            </Link>
            <Link
              to="/solutions/by-function/for-partnerships/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Partnerships (Embedded)
            </Link>
            <Link
              to="/solutions/by-function/for-finance/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Finance
            </Link>
            <Link
              to="/solutions/by-function/for-hr/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              HR & People Ops
            </Link>
            <Link
              to="/solutions/by-function/for-marketing/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Marketing
            </Link>
            <Link
              to="/solutions/by-function/for-sales/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Sales
            </Link>
            <Link
              to="/solutions/by-function/for-customer-support/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Customer Support
            </Link>
            <Link
              to="/solutions/by-function/for-logistics/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Supply Chain & Logistics
            </Link>
          </div>
        </div>
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">By Industry</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/solutions/by-industry/software-providers/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Software Providers
            </Link>
            <Link
              to="/solutions/by-industry/retail-and-commerce/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Retail & Commerce
            </Link>
            <Link
              to="/solutions/by-industry/fintechs/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Financial Services & Fintechs
            </Link>
            <Link
              to="/solutions/by-industry/commercial-banking/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Commercial Banking
            </Link>
            <Link
              to="/solutions/by-industry/shipping-and-logistics/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Shipping & Logistics
            </Link>
          </div>
        </div>
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">By Use Case</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/solutions/by-use-case/embedded-integrations/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Embedded Integrations
            </Link>
            <Link
              to="/solutions/by-use-case/agentic-ai/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Agentic AI
            </Link>
            <Link
              to="/solutions/by-use-case/order-to-cash/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Order to Cash
            </Link>
            <Link
              to="/solutions/by-use-case/ap-ar-reconciliation/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              AP/AR Reconciliation
            </Link>
            <Link
              to="/solutions/by-use-case/ecommerce-marketplace/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Ecommerce Marketplace
            </Link>
            <Link
              to="/solutions/by-use-case/bopis/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              BOPIS
            </Link>
            <Link
              to="/solutions/by-use-case/returns-management/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Returns Management
            </Link>
            <Link
              to="/solutions/by-use-case/expense-management/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              Expense Management
            </Link>
            <Link
              to="/solutions/by-use-case/3pl-and-logistics/"
              className="text-sm text-black hover:text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              3PL & Logistics Management
            </Link>
            <Link
              to="/solutions/by-use-case/"
              className="text-sm text-azure-800"
              onMouseEnter={() => setHoveredLink('')}
            >
              View All
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ResourcesContent = ({ blogs }) => {
  return (
    <div className="mx-auto flex max-w-7xl gap-[72px]">
      {/* Blog column */}
      <div className="w-3/4 text-sm">
        <h4 className="text-gray-900 mb-6 font-semibold">Blog</h4>
        <div className="grid grid-cols-3 gap-6">
          {blogs.map(({ node }, index) => (
            <Link
              to={`/blog/${node.slug}/`}
              className="group w-full overflow-hidden rounded-2xl border border-black/10 bg-white"
              key={index}
            >
              <div className="h-40 overflow-hidden border-b border-b-black/10">
                <img
                  src={convertUrltoCdn(node?.featured_image?.url)}
                  alt="blog-image"
                  className="h-full w-full bg-indigo-100 object-cover transition-smooth group-hover:scale-105"
                />
              </div>
              <div className="m-6 line-clamp-3 text-base font-medium text-black">
                {node.title}
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Menu columns */}
      <div className="w-1/4 gap-8 text-sm">
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">Resources</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/blog/"
              className="text-sm text-black hover:text-azure-800"
            >
              Blog
            </Link>
            <Link
              to="/guides/"
              className="text-sm text-black hover:text-azure-800"
            >
              Guides & Webinars
            </Link>
            <Link
              to="/calculating-app-actions/"
              className="text-sm text-black hover:text-azure-800"
            >
              Calculating App Actions
            </Link>
            <Link
              to="/can-alloy-do-x/"
              className="text-sm text-black hover:text-azure-800"
            >
              Can Alloy do that?
            </Link>
          </div>
        </div>
        <div className="mt-10">
          <h4 className="text-gray-900 mb-6 font-semibold">Developer Center</h4>
          <div className="flex flex-col space-y-2">
            <a
              href="https://docs.runalloy.com/"
              target="_blank"
              rel="nofollow noreferrer"
              className="text-sm text-black hover:text-azure-800"
            >
              Documentation
            </a>
            <a
              href="https://servicedesk.support.runalloy.com/servicedesk/customer/portals"
              target="_blank"
              rel="nofollow noreferrer"
              className="text-sm text-black hover:text-azure-800"
            >
              File a Support Ticket
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CaseStudiesContent = () => {
  return (
    <div className="mx-auto flex max-w-7xl gap-[72px]">
      {/* Cards column */}
      <div className="grid w-3/4 grid-cols-3 gap-4">
        <Link
          to="/case-studies/buy-with-prime/"
          className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#13163F]"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/case-studies/amazon.svg"
            alt="amazon"
            className="scale-[.6]"
          />
        </Link>
        <Link
          to="/case-studies/best-buy/"
          className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-azure-800"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/case-studies/best-buy.svg"
            alt="best-buy"
            className="scale-[.6]"
          />
        </Link>
        <Link
          to="/case-studies/typeform/"
          className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#111010]"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/case-studies/typeform-2.svg"
            alt="typeform"
            className="scale-[.6] invert"
          />
        </Link>
        <Link
          to="/case-studies/burberry/"
          className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/case-studies/burberry.svg"
            alt="burberry"
            className="scale-[.6]"
          />
        </Link>
        <Link
          to="/case-studies/postscript/"
          className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#5724E9]"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/case-studies/postscript-2.svg"
            alt="postscript"
            className="scale-75"
          />
        </Link>
        <Link
          to="/case-studies/loop-returns/"
          className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#1343D4]"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/case-studies/loop-returns.svg"
            alt="loop-returns"
            className="scale-75"
          />
        </Link>
      </div>

      {/* Menu columns */}
      <div className="w-1/4 text-sm">
        <div>
          <h4 className="text-gray-900 mb-6 font-semibold">Read More</h4>
          <div className="flex flex-col space-y-2">
            <Link
              to="/case-studies/gorgias/"
              className="text-sm text-black hover:text-azure-800"
            >
              Gorgias
            </Link>
            <Link
              to="/case-studies/emotive/"
              className="text-sm text-black hover:text-azure-800"
            >
              Emotive
            </Link>
            <Link
              to="/case-studies/shoplazza/"
              className="text-sm text-black hover:text-azure-800"
            >
              Shoplazza
            </Link>
            <Link to="/case-studies" className="text-sm text-azure-800">
              View All
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PartnersContent = () => {
  return (
    <div className="mx-auto flex max-w-7xl">
      <div className="grid w-full grid-cols-4 gap-4">
        <Link
          to="/"
          className="group relative flex h-36 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/icons/icon-alloy.webp"
            alt="overview"
            className="absolute left-2 top-2 h-16 w-16"
          />
          <div className="absolute bottom-4 left-5 flex items-center gap-1 text-base font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
            Overview <CaretRight size={16} weight="bold" />
          </div>
        </Link>
        <Link
          to="/"
          className="group relative flex h-36 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/icons/icon-link.webp"
            alt="overview"
            className="absolute left-2 top-2 h-16 w-16"
          />
          <div className="absolute bottom-4 left-5 flex items-center gap-1 text-base font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
            Become a partner <CaretRight size={16} weight="bold" />
          </div>
        </Link>
        <Link
          to="/"
          className="group relative flex h-36 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/icons/icon-bookmark.webp"
            alt="overview"
            className="absolute left-2 top-2 h-16 w-16"
          />
          <div className="absolute bottom-4 left-5 flex items-center gap-1 text-base font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
            Find a partner <CaretRight size={16} weight="bold" />
          </div>
        </Link>
        <Link
          to="/"
          className="group relative flex h-36 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
        >
          <img
            src="https://cdn.runalloy.com/landing/2025/icons/icon-globe.webp"
            alt="overview"
            className="absolute left-2 top-2 h-16 w-16"
          />
          <div className="absolute bottom-4 left-5 flex items-center gap-1 text-base font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
            Alliances <CaretRight size={16} weight="bold" />
          </div>
        </Link>
      </div>
    </div>
  );
};

export const MobileDrawer = props => {
  const items = [
    {
      key: '1',
      label: 'Platform',
      children: (
        <>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">Overview</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/platform/overview/"
                className="text-sm text-black hover:text-azure-800"
              >
                The Alloy Platform
              </Link>
              <Link
                to="/platform/why-alloy/"
                className="text-sm text-black hover:text-azure-800"
              >
                Why Alloy?
              </Link>
              <Link
                to="/platform/pricing/"
                className="text-sm text-black hover:text-azure-800"
              >
                Pricing
              </Link>
            </div>
          </div>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">Capabilities</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/integrations/"
                className="text-sm text-black hover:text-azure-800"
              >
                Connector Library
              </Link>
              <Link
                to="/platform/ai-agents/"
                className="text-sm text-black hover:text-azure-800"
              >
                AI Agents
              </Link>
              <Link
                to="/platform/on-premise/"
                className="text-sm text-black hover:text-azure-800"
              >
                On Premise
              </Link>
              <Link
                to="/platform/error-handling/"
                className="text-sm text-black hover:text-azure-800"
              >
                Error Handling & Logging
              </Link>
              <Link
                to="/platform/data-transformations/"
                className="text-sm text-black hover:text-azure-800"
              >
                ETL/ELT
              </Link>
              <Link
                to="/platform/bespoke-customizations/"
                className="text-sm text-black hover:text-azure-800"
              >
                Bespoke Customizations
              </Link>
              <Link
                to="/platform/mcp/"
                className="text-sm text-black hover:text-azure-800"
              >
                MCP
              </Link>
            </div>
          </div>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">Governance</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/platform/security/"
                className="text-sm text-black hover:text-azure-800"
              >
                Security and Compliance
              </Link>
              <Link
                to="/platform/data-governance/"
                className="text-sm text-black hover:text-azure-800"
              >
                Data Governance
              </Link>
              <Link
                to="/platform/scalability"
                className="text-sm text-black hover:text-azure-800"
              >
                Enterprise Scalability
              </Link>
            </div>
          </div>
          <div className="py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">Products</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/platform/embedded/"
                className="text-sm text-black hover:text-azure-800"
              >
                Embedded Integrations
              </Link>
              <Link
                to="/platform/enterprise-ipaas/"
                className="text-sm text-black hover:text-azure-800"
              >
                Enterprise iPaaS
              </Link>
            </div>
          </div>
        </>
      ),
    },
    {
      key: '2',
      label: 'Solutions',
      children: (
        <>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">
              Popular Integrations
            </h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/solutions/popular-integrations/netsuite/"
                className="text-sm text-black hover:text-azure-800"
              >
                NetSuite
              </Link>
              <Link
                to="/solutions/popular-integrations/sap-s4hana/"
                className="text-sm text-black hover:text-azure-800"
              >
                SAP (S/4HANA)
              </Link>
              <Link
                to="/solutions/popular-integrations/microsoft-dynamics-365-business-central/"
                className="text-sm text-black hover:text-azure-800"
              >
                Microsoft Dynamics 365
              </Link>
              <Link
                to="/solutions/popular-integrations/shopify/"
                className="text-sm text-black hover:text-azure-800"
              >
                Shopify
              </Link>
              <Link
                to="/solutions/popular-integrations/amazon/"
                className="text-sm text-black hover:text-azure-800"
              >
                Amazon
              </Link>
              <Link
                to="/solutions/popular-integrations/adp-workforce-now/"
                className="text-sm text-black hover:text-azure-800"
              >
                ADP
              </Link>
              <Link
                to="/solutions/popular-integrations/service-now/"
                className="text-sm text-black hover:text-azure-800"
              >
                Service Now
              </Link>
              <Link
                to="/solutions/popular-integrations/salesforce/"
                className="text-sm text-black hover:text-azure-800"
              >
                Salesforce
              </Link>
              <Link
                to="/solutions/popular-integrations/workday/"
                className="text-sm text-black hover:text-azure-800"
              >
                Workday
              </Link>
              <Link to="/integrations/" className="text-sm text-azure-800">
                View All
              </Link>
            </div>
          </div>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">By Function</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/solutions/by-function/for-it/"
                className="text-sm text-black hover:text-azure-800"
              >
                IT
              </Link>
              <Link
                to="/solutions/by-function/for-product/"
                className="text-sm text-black hover:text-azure-800"
              >
                Product (Embedded)
              </Link>
              <Link
                to="/solutions/by-function/for-partnerships/"
                className="text-sm text-black hover:text-azure-800"
              >
                Partnerships (Embedded)
              </Link>
              <Link
                to="/solutions/by-function/for-finance/"
                className="text-sm text-black hover:text-azure-800"
              >
                Finance
              </Link>
              <Link
                to="/solutions/by-function/for-hr/"
                className="text-sm text-black hover:text-azure-800"
              >
                HR & People Ops
              </Link>
              <Link
                to="/solutions/by-function/for-marketing/"
                className="text-sm text-black hover:text-azure-800"
              >
                Marketing
              </Link>
              <Link
                to="/solutions/by-function/for-sales/"
                className="text-sm text-black hover:text-azure-800"
              >
                Sales
              </Link>
              <Link
                to="/solutions/by-function/for-customer-support/"
                className="text-sm text-black hover:text-azure-800"
              >
                Customer Support
              </Link>
              <Link
                to="/solutions/by-function/for-logistics/"
                className="text-sm text-black hover:text-azure-800"
              >
                Supply Chain & Logistics
              </Link>
            </div>
          </div>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">By Industry</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/solutions/by-industry/software-providers/"
                className="text-sm text-black hover:text-azure-800"
              >
                Software Providers
              </Link>
              <Link
                to="/solutions/by-industry/retail-and-commerce/"
                className="text-sm text-black hover:text-azure-800"
              >
                Retail & Commerce
              </Link>
              <Link
                to="/solutions/by-industry/fintechs/"
                className="text-sm text-black hover:text-azure-800"
              >
                Financial Services & Fintechs
              </Link>
              <Link
                to="/solutions/by-industry/commercial-banking/"
                className="text-sm text-black hover:text-azure-800"
              >
                Commercial Banking
              </Link>
              <Link
                to="/solutions/by-industry/shipping-and-logistics/"
                className="text-sm text-black hover:text-azure-800"
              >
                Shipping & Logistics
              </Link>
            </div>
          </div>
          <div className="py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">By Use Case</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/solutions/by-use-case/embedded-integrations/"
                className="text-sm text-black hover:text-azure-800"
              >
                Embedded Integrations
              </Link>
              <Link
                to="/solutions/by-use-case/agentic-ai/"
                className="text-sm text-black hover:text-azure-800"
              >
                Agentic AI
              </Link>
              <Link
                to="/solutions/by-use-case/order-to-cash/"
                className="text-sm text-black hover:text-azure-800"
              >
                Order to Cash
              </Link>
              <Link
                to="/solutions/by-use-case/ap-ar-reconciliation/"
                className="text-sm text-black hover:text-azure-800"
              >
                AP/AR Reconciliation
              </Link>
              <Link
                to="/solutions/by-use-case/ecommerce-marketplace/"
                className="text-sm text-black hover:text-azure-800"
              >
                Ecommerce Marketplace
              </Link>
              <Link
                to="/solutions/by-use-case/bopis/"
                className="text-sm text-black hover:text-azure-800"
              >
                BOPIS
              </Link>
              <Link
                to="/solutions/by-use-case/returns-management/"
                className="text-sm text-black hover:text-azure-800"
              >
                Returns Management
              </Link>
              <Link
                to="/solutions/by-use-case/expense-management/"
                className="text-sm text-black hover:text-azure-800"
              >
                Expense Management
              </Link>
              <Link
                to="/solutions/by-use-case/3pl-and-logistics/"
                className="text-sm text-black hover:text-azure-800"
              >
                3PL & Logistics Management
              </Link>
              <Link
                to="/solutions/by-use-case/"
                className="text-sm text-azure-800"
              >
                View All
              </Link>
            </div>
          </div>
        </>
      ),
    },
    {
      key: '3',
      label: 'Resources',
      children: (
        <>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-6 font-semibold">Blog</h4>
            <div className="grid grid-cols-2 gap-4">
              {props.blogs.slice(0, 2).map(({ node }, index) => (
                <Link
                  to={`/blog/${node.slug}/`}
                  className="group w-full overflow-hidden rounded-2xl border border-black/10 bg-white"
                  key={index}
                >
                  <div className="h-28 overflow-hidden border-b border-b-black/10">
                    <img
                      src={convertUrltoCdn(node?.featured_image?.url)}
                      alt="blog-image"
                      className="h-full w-full bg-indigo-100 object-cover transition-smooth group-hover:scale-105"
                    />
                  </div>
                  <div className="m-3 line-clamp-3 text-sm font-medium text-black">
                    {node.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="border-b border-b-black/10 py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">Resources</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/blog/"
                className="text-sm text-black hover:text-azure-800"
              >
                Blog
              </Link>
              <Link
                to="/guides/"
                className="text-sm text-black hover:text-azure-800"
              >
                Guides & Webinars
              </Link>
              <Link
                to="/calculating-app-actions/"
                className="text-sm text-black hover:text-azure-800"
              >
                Calculating App Actions
              </Link>
              <Link
                to="/can-alloy-do-x/"
                className="text-sm text-black hover:text-azure-800"
              >
                Can Alloy do that?
              </Link>
            </div>
          </div>
          <div className="py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">
              Developer Center
            </h4>
            <div className="flex flex-col space-y-1">
              <a
                href="https://docs.runalloy.com/"
                target="_blank"
                rel="nofollow noreferrer"
                className="text-sm text-black hover:text-azure-800"
              >
                Documentation
              </a>
              <a
                href="https://servicedesk.support.runalloy.com/servicedesk/customer/portals"
                target="_blank"
                rel="nofollow noreferrer"
                className="text-sm text-black hover:text-azure-800"
              >
                File a Support Ticket
              </a>
            </div>
          </div>
        </>
      ),
    },
    {
      key: '4',
      label: 'Case Studies',
      children: (
        <>
          <div className="grid grid-cols-2 gap-4 border-b border-b-black/10 py-6">
            <Link
              to="/case-studies/buy-with-prime/"
              className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#13163F]"
            >
              <img
                src="https://cdn.runalloy.com/landing/2025/case-studies/amazon.svg"
                alt="amazon"
                className="scale-[.6]"
              />
            </Link>
            <Link
              to="/case-studies/best-buy/"
              className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-azure-800"
            >
              <img
                src="https://cdn.runalloy.com/landing/2025/case-studies/best-buy.svg"
                alt="best-buy"
                className="scale-[.6]"
              />
            </Link>
            <Link
              to="/case-studies/typeform/"
              className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#111010]"
            >
              <img
                src="https://cdn.runalloy.com/landing/2025/case-studies/typeform-2.svg"
                alt="typeform"
                className="scale-[.6] invert"
              />
            </Link>
            <Link
              to="/case-studies/burberry/"
              className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black"
            >
              <img
                src="https://cdn.runalloy.com/landing/2025/case-studies/burberry.svg"
                alt="burberry"
                className="scale-[.6]"
              />
            </Link>
            <Link
              to="/case-studies/postscript/"
              className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#5724E9]"
            >
              <img
                src="https://cdn.runalloy.com/landing/2025/case-studies/postscript-2.svg"
                alt="postscript"
                className="scale-75"
              />
            </Link>
            <Link
              to="/case-studies/loop-returns/"
              className="relative flex h-32 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-[#1343D4]"
            >
              <img
                src="https://cdn.runalloy.com/landing/2025/case-studies/loop-returns.svg"
                alt="loop-returns"
                className="scale-75"
              />
            </Link>
          </div>
          <div className="py-6">
            <h4 className="text-gray-900 mb-4 font-semibold">Read More</h4>
            <div className="flex flex-col space-y-1">
              <Link
                to="/case-studies/gorgias/"
                className="text-sm text-black hover:text-azure-800"
              >
                Gorgias
              </Link>
              <Link
                to="/case-studies/emotive/"
                className="text-sm text-black hover:text-azure-800"
              >
                Emotive
              </Link>
              <Link
                to="/case-studies/shoplazza/"
                className="text-sm text-black hover:text-azure-800"
              >
                Shoplazza
              </Link>
              <Link to="/case-studies" className="text-sm text-azure-800">
                View All
              </Link>
            </div>
          </div>
        </>
      ),
    },
    // {
    //   key: '5',
    //   label: 'Partners',
    //   children: (
    //     <>
    //       <div className="py-6">
    //         <div className="grid w-full grid-cols-2 gap-4">
    //           <Link
    //             to="/"
    //             className="group relative flex h-28 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
    //           >
    //             <img
    //               src="https://cdn.runalloy.com/landing/2025/icons/icon-alloy.webp"
    //               alt="overview"
    //               className="absolute left-2 top-2 h-10 w-10"
    //             />
    //             <div className="absolute bottom-3 left-3 flex items-center gap-1 text-sm font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
    //               Overview <CaretRight size={14} weight="bold" />
    //             </div>
    //           </Link>
    //           <Link
    //             to="/"
    //             className="group relative flex h-28 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
    //           >
    //             <img
    //               src="https://cdn.runalloy.com/landing/2025/icons/icon-link.webp"
    //               alt="overview"
    //               className="absolute left-2 top-2 h-10 w-10"
    //             />
    //             <div className="absolute bottom-3 left-3 flex items-center gap-1 text-sm font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
    //               Become a partner <CaretRight size={14} weight="bold" />
    //             </div>
    //           </Link>
    //           <Link
    //             to="/"
    //             className="group relative flex h-28 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
    //           >
    //             <img
    //               src="https://cdn.runalloy.com/landing/2025/icons/icon-bookmark.webp"
    //               alt="overview"
    //               className="absolute left-2 top-2 h-10 w-10"
    //             />
    //             <div className="absolute bottom-3 left-3 flex items-center gap-1 text-sm font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
    //               Find a partner <CaretRight size={14} weight="bold" />
    //             </div>
    //           </Link>
    //           <Link
    //             to="/"
    //             className="group relative flex h-28 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-black/5 transition-smooth hover:bg-white"
    //           >
    //             <img
    //               src="https://cdn.runalloy.com/landing/2025/icons/icon-globe.webp"
    //               alt="overview"
    //               className="absolute left-2 top-2 h-10 w-10"
    //             />
    //             <div className="absolute bottom-3 left-3 flex items-center gap-1 text-sm font-bold text-black transition-smooth group-hover:text-azure-800 [&_svg_path]:transition-smooth group-hover:[&_svg_path]:!fill-azure-800">
    //               Alliances <CaretRight size={14} weight="bold" />
    //             </div>
    //           </Link>
    //         </div>
    //       </div>
    //     </>
    //   ),
    // },
  ];

  return (
    <div
      className={classNames(
        'fixed bottom-0 left-0 right-0 top-12 z-[1100] h-[calc(100vh-48px)] border-t border-t-black/10 bg-white transition-smooth',
        {
          'translate-x-0': props.visible,
          'translate-x-full': !props.visible,
        }
      )}
    >
      <div className="flex h-full flex-col overflow-auto px-4 pb-16">
        {items.map(item => {
          return (
            <div key={item.key}>
              <input
                className="peer hidden"
                id={kebabCase(item.label)}
                type="checkbox"
              />
              <label
                className="flex items-center justify-between border-b border-b-black/10 py-4 text-base font-semibold text-black/80 sm:border-none sm:py-0 sm:text-sm peer-checked:[&_div]:-rotate-180"
                htmlFor={kebabCase(item.label)}
              >
                {item.label}
                <div className="block transition-smooth sm:hidden [&_svg]:scale-125">
                  {CARET_DOWN}
                </div>
              </label>
              <div className="max-h-0 overflow-hidden transition-smooth peer-checked:max-h-full">
                {item.children}
              </div>
            </div>
          );
        })}
      </div>
      <div className="fixed bottom-0 left-0 h-12 w-full p-1">
        <a
          className="flex h-full items-center justify-center rounded-sm bg-azure-800 px-4 py-2 text-sm font-medium text-white transition-smooth hover:bg-azure-950 hover:!text-white"
          href={`/book-demo/${getUtmSource()}`}
        >
          Book a demo
        </a>
      </div>
    </div>
  );
};
